import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Flip Card Digit Component
const FlipDigit = ({ digit, label }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div className="flip-card bg-[#003456] rounded-xl w-16 h-20 md:w-24 md:h-28 flex items-center justify-center shadow-2xl overflow-hidden border border-[#7b97c9]/30">
          <span className="text-3xl md:text-5xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono' }}>
            {digit.toString().padStart(2, '0')}
          </span>
          {/* Flip line */}
          <div className="absolute inset-x-0 top-1/2 h-[1px] bg-black/30"></div>
          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
        </div>
      </div>
      <span className="text-xs md:text-sm text-white/60 mt-3 uppercase tracking-widest font-medium">
        {label}
      </span>
    </div>
  );
};

const ComingSoonPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Launch date: March 10th, 2026
  const launchDate = new Date('2026-03-10T00:00:00');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = launchDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { 
        email,
        interests: ['launch-notification']
      });
      setSubscribed(true);
      toast.success("You're on the list! We'll notify you when we launch.");
    } catch (error) {
      if (error.response?.data?.detail === 'Email already subscribed') {
        toast.success("You're already on the list!");
        setSubscribed(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=1920&q=80')`
        }}
      >
        {/* Overlay - Navy Blue gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0203]/95 via-[#003456]/90 to-[#7b97c9]/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="py-6 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-center">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_76b91bca-f3f4-4613-9a08-ad8c90cec57e/artifacts/xiemrfmz_HomeLyph_Chrome_Logo%20%281%29.png" 
                alt="HomeLyph Logo" 
                className="h-48 md:h-64 lg:h-72 w-auto"
              />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#003456]/50 backdrop-blur-sm border border-[#fdc31c]/40 px-4 py-2 rounded-full mb-8">
              <div className="w-2 h-2 bg-[#fdc31c] rounded-full animate-pulse"></div>
              <span className="text-[#fdc31c] text-sm font-medium">Coming March 10th, 2026</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Preserve Life's
              <span className="block text-[#fdc31c]">Best Moments</span>
            </h1>

            <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-12">
              Premium home freeze dryers are coming. Preserve your food naturally for up to 25 years. 
              Be the first to know when we launch.
            </p>

            {/* Countdown */}
            <div className="flex items-center justify-center gap-3 md:gap-6 mb-12">
              <FlipDigit digit={timeLeft.days} label="Days" />
              <span className="text-3xl md:text-5xl text-white/30 font-light -mt-6">:</span>
              <FlipDigit digit={timeLeft.hours} label="Hours" />
              <span className="text-3xl md:text-5xl text-white/30 font-light -mt-6">:</span>
              <FlipDigit digit={timeLeft.minutes} label="Minutes" />
              <span className="text-3xl md:text-5xl text-white/30 font-light -mt-6">:</span>
              <FlipDigit digit={timeLeft.seconds} label="Seconds" />
            </div>

            {/* Email Signup */}
            {subscribed ? (
              <div className="flex items-center justify-center gap-3 bg-[#003456]/50 backdrop-blur-sm border border-[#fdc31c]/40 px-6 py-4 rounded-2xl max-w-md mx-auto">
                <CheckCircle className="h-6 w-6 text-[#fdc31c]" />
                <span className="text-white font-medium">You're on the list! We'll email you at launch.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="max-w-md mx-auto">
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-xl focus:border-[#fdc31c] focus:ring-[#fdc31c]"
                      data-testid="coming-soon-email"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-semibold px-6 py-6 rounded-xl"
                    data-testid="coming-soon-notify-btn"
                  >
                    {loading ? '...' : 'Notify Me'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <p className="text-white/40 text-sm mt-4">
                  Join 1,000+ others waiting for launch. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-white/30 text-sm">
              &copy; {new Date().getFullYear()} HomeLyph. All rights reserved.
            </p>
          </div>
        </footer>
      </div>

      {/* CSS for flip animation */}
      <style>{`
        .flip-card {
          perspective: 1000px;
          transform-style: preserve-3d;
        }
        
        @keyframes flip {
          0% { transform: rotateX(0deg); }
          50% { transform: rotateX(-90deg); }
          100% { transform: rotateX(0deg); }
        }
      `}</style>
    </div>
  );
};

export default ComingSoonPage;
